import parsePhoneNumber, { getCountryCallingCode } from 'libphonenumber-js';

/**
 * Phone-number helpers, deliberately kept out of util/validators.js.
 *
 * `libphonenumber-js` ships ~117 KB of country metadata. validators.js is imported by 65 modules
 * across the app — including ones reachable from the main entry — so having these two functions
 * live there put that metadata in the main bundle for every page load, including pages with no
 * phone field anywhere on them. Every caller of these helpers is inside CheckoutPage or
 * AuthenticationPage, both of which are lazy route chunks, so isolating them here confines the
 * metadata to those two chunks.
 *
 * Keep this module free of imports from validators.js (and vice versa) so the split holds —
 * which is why VALID is redeclared here rather than shared, matching validators.js's own
 * `const VALID = undefined` convention.
 */
const VALID = undefined;

/**
 * Normalize raw phone input for a given country so the result is a single E.164-style string.
 * - Strips non-digits from input.
 * - If the digits start with the country's calling code (e.g. "1" for US/CA), removes that
 *   prefix so we don't double the country code when prepending it.
 * This prevents values like "+1+34687580271" when user pastes "+34687580271" and country is CA.
 *
 * @param {string} countryCode - Two-letter country code (e.g. 'CA', 'US')
 * @param {string} rawInput - Whatever the user typed (may include +, spaces, or another country code)
 * @returns {string} E.164-style number: '+' + countryCallingCode + nationalDigits
 */
export const normalizePhoneForCountry = (countryCode, rawInput) => {
  if (!rawInput || typeof rawInput !== 'string') return '';
  const digits = rawInput.replace(/\D/g, '');
  if (digits.length === 0) return '';
  let codeDigits;
  try {
    codeDigits = getCountryCallingCode(countryCode);
  } catch {
    return `+${digits}`;
  }
  const nationalPart = digits.startsWith(codeDigits) ? digits.slice(codeDigits.length) : digits;
  return `+${codeDigits}${nationalPart}`;
};

export const phoneNumberValidator = (message, country) => value => {
  // If value is empty or null, consider it valid (since phone is optional)
  if (!value || value.trim() === '') {
    return VALID;
  }

  // Validate the normalized number we will actually save (avoids double country code / wrong length)
  const normalized = normalizePhoneForCountry(country, value);
  const phoneNumber = parsePhoneNumber(normalized, country);
  return phoneNumber && phoneNumber.isValid() ? VALID : message;
};
